import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  serverTimestamp,
} from 'firebase/firestore';

/**
 * Manages PDF highlight data in Firestore for the current user.
 * Handles real-time updates and automatically manages the connection
 * based on page visibility to conserve resources.
 */
class HighlightManager {
  /**
   * @param {object} db - The initialized Firestore database instance.
   * @param {object} auth - The initialized Firebase Auth instance.
   */
  constructor(db, auth) {
    if (!db || !auth) {
      throw new Error('Firestore DB and Auth instances are required.');
    }
    this.db = db;
    this.auth = auth;
    this.unsubscribe = null; // To hold the listener's unsubscribe function
    this.updateCallback = null; // The callback function to notify the UI of changes

    // Bind the context of `this` for the event handler
    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
  }

  /**
   * Gets a reference to the current user's highlight collection.
   * Path: BookMarks/{userId}/collection/
   * @private
   * @returns {object|null} A Firestore collection reference or null if the user is not authenticated.
   */
  _getHighlightsCollectionRef() {
    const userId = this.auth.currentUser?.uid;
    if (!userId) {
      console.error('User not authenticated. Cannot access highlights.');
      return null;
    }
    // Path as requested: BookMarks/${auth.currentUser.uid}/collection
    return collection(this.db, 'BookMarks', userId, 'collection');
  }

  /**
   * Starts listening for real-time updates to the user's highlights.
   * When changes occur, it invokes the provided callback with the complete list of highlights.
   * Also sets up visibility change handlers to manage the connection.
   * @param {function} callback - A function to be called with the array of highlights whenever they are updated.
   */
  listenForHighlights(callback) {
    this.updateCallback = callback;
    this._startListener();
    // Add a single event listener to manage the connection
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
  }

  /**
   * Permanently stops the Firestore listener and cleans up the visibility handler.
   */
  stopListening() {
    this._stopListener();
    document.removeEventListener(
      'visibilitychange',
      this._handleVisibilityChange
    );
    console.log('Highlight listener permanently stopped.');
  }

  /**
   * Attaches the onSnapshot listener to Firestore.
   * @private
   */
  _startListener() {
    // Prevent creating duplicate listeners
    if (this.unsubscribe) {
      return;
    }

    const collectionRef = this._getHighlightsCollectionRef();
    if (!collectionRef) return;

    console.log('Starting Firestore listener for highlights...');
    const q = query(collectionRef);

    this.unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const highlights = [];
        querySnapshot.forEach((doc) => {
          // The document ID from Firestore is the same as our client-side `id`
          highlights.push({ id: doc.id, ...doc.data() });
        });

        console.log(
          `Highlights updated from Firestore. Source: ${querySnapshot.metadata.fromCache ? 'Cache' : 'Server'}`
        );

        // Notify the main application with the fresh data
        if (this.updateCallback) {
          this.updateCallback(highlights);
        }
      },
      (error) => {
        console.error('Error in Firestore listener:', error);
      }
    );
  }

  /**
   * Detaches the onSnapshot listener.
   * @private
   */
  _stopListener() {
    if (this.unsubscribe) {
      console.log('Stopping Firestore listener due to page inactivity.');
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  /**
   * Handles the browser's visibility change event to pause or resume the listener.
   * @private
   */
  _handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this._stopListener();
    } else {
      this._startListener();
    }
  }

  /**
   * Adds or updates a highlight in Firestore.
   * It uses the client-generated `id` from the highlight object as the document ID in Firestore.
   * @param {object} highlightData - The highlight object. Must contain a unique `id` property.
   * @returns {Promise<string|null>} The ID of the highlight or null on failure.
   */
  async addHighlight(highlightData) {
    const collectionRef = this._getHighlightsCollectionRef();
    if (!collectionRef) return null;

    try {
      const { id, ...dataToStore } = highlightData; // Separate the ID from the rest of the data
      if (!id)
        throw new Error("Highlight data must have a unique 'id' property.");

      const docRef = doc(collectionRef, id); // Create a document reference with our custom ID
      dataToStore.uid = this.auth.currentUser.uid; // Ensure the user ID is included
      dataToStore.created = serverTimestamp(); // Add a timestamp for creation
      await setDoc(docRef, dataToStore); // Use setDoc to save the document

      console.log('Highlight saved to Firestore with ID: ', id);
      return id;
    } catch (error) {
      console.error('Error adding highlight to Firestore: ', error);
      return null;
    }
  }

  /**
   * Deletes a highlight from Firestore using its ID.
   * @param {string} highlightId - The unique ID of the highlight to delete.
   * @returns {Promise<void>}
   */
  async deleteHighlight(highlightId) {
    const collectionRef = this._getHighlightsCollectionRef();
    if (!collectionRef) return;

    try {
      const docRef = doc(collectionRef, highlightId);
      await deleteDoc(docRef);
      console.log('Highlight deleted from Firestore: ', highlightId);
    } catch (error) {
      console.error('Error deleting highlight from Firestore: ', error);
    }
  }
}

// Export the class to be used as a module
export { HighlightManager };
